import React, { useEffect, useState } from 'react';
import azureDevOpsService from './services/azureDevOpsService';

export default function KpiSquadSocleFrontMTTD() {
  const [mttd, setMttd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState('');
  const [pbiDetails, setPbiDetails] = useState([]);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      
      try {
        // Récupérer tous les PBIs de Squad Socle Front
        setProgress('Récupération des PBIs Squad Socle Front...');
        const pbis = await azureDevOpsService.getSquadSocleFrontPBIs();
        
        if (pbis.length === 0) {
          setProgress('Aucun PBI trouvé');
          setLoading(false);
          return;
        }

        // Filtrer les PBIs qui ont atteint le statut 5- Validation ou 6- Done
        const donePbis = pbis.filter(pbi => pbi.state === '5- Validation' || pbi.state === '6- Done');
        
        if (donePbis.length === 0) {
          setProgress(`${pbis.length} PBIs trouvés, aucun en "5- Validation" ou "6- Done"`);
          setLoading(false);
          return;
        }

        // Calculer le MTTD (temps entre 2- Dev et 5- Validation/6- Done)
        const mttdValues = [];
        const pbiDetailsData = [];
        
        setProgress(`Traitement de ${donePbis.length} PBIs en Done...`);
        
        // Traiter les PBIs par batch de 5 pour éviter trop d'appels simultanés
        const batchSize = 5;
        for (let i = 0; i < donePbis.length; i += batchSize) {
          const batch = donePbis.slice(i, i + batchSize);
          
          setProgress(`Traitement des PBIs ${i + 1} à ${Math.min(i + batchSize, donePbis.length)} sur ${donePbis.length}...`);
          
          await Promise.all(batch.map(async (pbi) => {
            try {
              // Récupérer la date de passage en "2- Dev"
              const devDate = await azureDevOpsService.getPBIStatusChangeDate(pbi.id, '2- Dev');
              
              // Récupérer la date de passage en "5- Validation" ou "6- Done" (en priorité 5- Validation)
              let transitionDate = await azureDevOpsService.getPBIStatusChangeDate(pbi.id, '5- Validation');
              
              // Si pas de 5- Validation, chercher 6- Done
              if (!transitionDate) {
                transitionDate = await azureDevOpsService.getPBIStatusChangeDate(pbi.id, '6- Done');
              }
              
              if (devDate && transitionDate) {
                const devDateObj = new Date(devDate);
                const transitionDateObj = new Date(transitionDate);
                
                // Calculer le temps en jours
                const timeInDays = (transitionDateObj - devDateObj) / (1000 * 60 * 60 * 24);
                
                if (timeInDays >= 0) {
                  mttdValues.push(timeInDays);
                  pbiDetailsData.push({
                    id: pbi.id,
                    title: pbi.title,
                    devDate: devDateObj.toLocaleDateString('fr-FR'),
                    doneDate: transitionDateObj.toLocaleDateString('fr-FR'),
                    mttd: timeInDays.toFixed(1),
                    url: pbi.url
                  });
                } else {
                  console.warn(`PBI ${pbi.id}: Date Dev postérieure à Date de transition`);
                }
              }
            } catch (error) {
              console.error(`Erreur traitement PBI ${pbi.id}:`, error);
            }
          }));
        }

        setProgress('Calcul du résultat final...');
        
        if (mttdValues.length > 0) {
          const averageMttd = mttdValues.reduce((a, b) => a + b, 0) / mttdValues.length;
          const minMttd = Math.min(...mttdValues);
          const maxMttd = Math.max(...mttdValues);
          
          setMttd({
            average: averageMttd.toFixed(1),
            min: minMttd.toFixed(1),
            max: maxMttd.toFixed(1),
            count: mttdValues.length,
            totalPbis: donePbis.length
          });
          
          // Trier les PBIs par MTTD décroissant
          pbiDetailsData.sort((a, b) => parseFloat(b.mttd) - parseFloat(a.mttd));
          setPbiDetails(pbiDetailsData);
        } else {
          setProgress('Aucun PBI avec données de transitions valides trouvé');
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Erreur récupération données MTTD:', error);
        setProgress('Erreur lors de la récupération des données');
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  return (
    <div className="bg-white rounded shadow p-6">
      <h2 className="text-lg font-bold mb-4">MTTD - Squad Socle Front (2- Dev → 5- Validation/6- Done)</h2>
      
      {loading || !mttd ? (
        <div>
          <div className="text-gray-600">Chargement...</div>
          {progress && <div className="text-sm text-gray-500 mt-2">{progress}</div>}
        </div>
      ) : (
        <div>
          {/* Résumé des métriques */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 rounded p-4">
              <div className="text-sm text-gray-600 mb-1">Temps moyen (MTTD)</div>
              <div className="text-2xl font-bold text-blue-600">{mttd.average}</div>
              <div className="text-xs text-gray-500">jours</div>
            </div>
            
            <div className="bg-green-50 rounded p-4">
              <div className="text-sm text-gray-600 mb-1">Minimum</div>
              <div className="text-2xl font-bold text-green-600">{mttd.min}</div>
              <div className="text-xs text-gray-500">jours</div>
            </div>
            
            <div className="bg-red-50 rounded p-4">
              <div className="text-sm text-gray-600 mb-1">Maximum</div>
              <div className="text-2xl font-bold text-red-600">{mttd.max}</div>
              <div className="text-xs text-gray-500">jours</div>
            </div>
            
            <div className="bg-purple-50 rounded p-4">
              <div className="text-sm text-gray-600 mb-1">PBIs analysés</div>
              <div className="text-2xl font-bold text-purple-600">{mttd.count}</div>
              <div className="text-xs text-gray-500">sur {mttd.totalPbis}</div>
            </div>
          </div>

          {/* Détail des PBIs */}
          {pbiDetails.length > 0 && (
            <div>
              <h3 className="text-md font-semibold mb-3">Détail des PBIs</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100 border-b">
                      <th className="px-4 py-2 text-left">ID</th>
                      <th className="px-4 py-2 text-left">Titre</th>
                      <th className="px-4 py-2 text-center">Date Dev</th>
                      <th className="px-4 py-2 text-center">Date Done</th>
                      <th className="px-4 py-2 text-center font-bold text-blue-600">TTD (jours)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pbiDetails.map((pbi) => (
                      <tr key={pbi.id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-2">
                          <a 
                            href={pbi.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline font-medium"
                          >
                            #{pbi.id}
                          </a>
                        </td>
                        <td className="px-4 py-2 max-w-xs truncate" title={pbi.title}>{pbi.title}</td>
                        <td className="px-4 py-2 text-center text-gray-600">{pbi.devDate}</td>
                        <td className="px-4 py-2 text-center text-gray-600">{pbi.doneDate}</td>
                        <td className="px-4 py-2 text-center font-bold text-blue-600">{pbi.mttd}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
